import React from "react";
import { Icon } from "@iconify/react";
import "./../../../assets/css/purchase/datatable.css";

export default function ContactTable({
  contacts = [],
  onEdit,
  onDelete,
  onAdd,
  customerType = "corporate",
  showAddButton = false,
}) {
  const showPosition = customerType === "corporate";

  const handleDelete = (contact) => {
    if (contact.isMain) return;
    onDelete?.(contact);
  };

  const getContactName = (c) => {
    const name = c.AR_ContactName?.trim()
      ? c.AR_ContactName
      : `${c.AR_Name || ""} ${c.AR_LastName || ""}`.trim();
    return name || "-";
  };

  const formatPhoneNumber = (tel = "") => {
    if (!tel) return "-";
    if (tel.includes("-")) return tel;
    const cleaned = tel.replace(/\D/g, "");
    if (cleaned.length === 10)
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    if (cleaned.length === 9)
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 5)}-${cleaned.slice(5)}`;
    return tel;
  };

  return (
    <div
      className="card table-card border-0 rounded-0 shadow-sm pl-3"
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        * { font-size: 14px !important; }

        table.table {
          border-collapse: separate !important;
          border-spacing: 0 !important;
          width: 100%;
          min-width: 1100px;
        }

        th, td {
          text-align: center !important;
          vertical-align: middle !important;
          border: 1px solid #ccc !important;
          padding: 6px 10px !important;
          white-space: nowrap;
        }

        thead th {
          background-color: #E13F57 !important;
          color: white !important;
          font-weight: 600;
        }

        tbody tr:nth-child(odd) { background: #fafafa; }
        tbody tr:nth-child(even) { background: #fff; }
        tbody tr:hover { background: #f5f7ff !important; }

        td.sticky-action, th.sticky-action {
          position: sticky !important;
          right: 0 !important;
          background: white !important;
          z-index: 20 !important;
          isolation: isolate;
          box-shadow: -3px 0 5px rgba(0,0,0,0.1);
        }

        thead th.sticky-action {
          background-color: #E13F57 !important;
          z-index: 21 !important;
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
        <h5 className="m-0 fw-bold">ข้อมูลการติดต่อ</h5>
        {showAddButton && (
          <button
            type="button"
            onClick={onAdd}
            className="flex items-center gap-1 text-[#F17F25] text-sm font-semibold border-b-2 border-[#F17F25] pb-0.5 hover:text-[#c7641c] transition"
          >
            <Icon icon="mdi:plus-circle-outline" className="w-5 h-5" /> เพิ่มข้อมูลการติดต่อ
          </button>
        )}
      </div>

      <div
        className="table-wrapper-scroll"
        style={{
          overflowY: "auto",
          overflowX: "auto",
          maxHeight: "180px",
          padding: "0",
        }}
      >
        <table className="table table-bordered table-hover mb-0">
          <thead>
            <tr className="text-center">
              <th style={{ width: "60px" }}>ลำดับ</th>
              <th style={{ width: "20%" }}>สถานที่บริการ</th>
              <th>ผู้ติดต่อ</th>
              {showPosition && <th>ตำแหน่งผู้ติดต่อ</th>}
              <th style={{ width: "15%" }}>เบอร์โทร</th>
              <th style={{ width: "15%" }}>เบอร์มือถือ</th>
              <th style={{ width: "20%" }}>อีเมล</th>
              <th style={{ width: "15%" }}>ไลน์</th>
              <th className="sticky-action" style={{ width: "10%" }}>การจัดการ</th>
            </tr>
          </thead>

          <tbody>
            {contacts.length > 0 ? (
              contacts.map((c, i) => (
                <tr key={c.ServiceContact_Id ?? c.AR_ContactId ?? `contact-${i}`}>
                  <td>{i + 1}</td>
                  <td className="text-start">
                    {c.ServiceAddr_Name || "-"}
                  </td>

                  <td className="text-start">
                    <div className="flex items-center gap-2">
                      {c.isMain && (
                        <span className="inline-block text-xs text-white bg-[#F17F25] rounded px-2 py-0.5">
                          ผู้ติดต่อหลัก
                        </span>
                      )}
                      <span>{getContactName(c)}</span>
                    </div>
                  </td>

                  {showPosition && (
                    <td className="text-start">{c.AR_ContactPosition || "-"}</td>
                  )}

                  <td>{formatPhoneNumber(c.AR_Tel)}</td>
                  <td>{formatPhoneNumber(c.AR_Mobile)}</td>
                  <td className="text-start">{c.AR_Email || "-"}</td>
                  <td>{c.AR_Line || "-"}</td>

                  <td className="text-center sticky-action">
                    {!c.isMain ? (
                      <div className="d-flex justify-content-center gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit?.(c, c.ServiceAddr_Id)}
                          className="btn btn-sm text-white"
                          style={{ backgroundColor: "#598AE4" }}
                        >
                          แก้ไข
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(c)}
                          className="btn btn-sm text-white"
                          style={{ backgroundColor: "#E13F57" }}
                        >
                          ลบ
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">-</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={showPosition ? 8 : 7}
                  className="text-center py-3 text-muted"
                >
                  ไม่มีข้อมูลการติดต่อ
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-start py-2">
        จำนวนข้อมูลทั้งหมด{" "}
        <span className="text-red-600 font-bold">{contacts.length}</span>{" "}
        รายการ
      </div>
    </div>
  );
}
